// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { redirectToLogout } from '../services/authApi';
import axios from 'axios';

// Helper function to safely get user data from localStorage
const getInitialUser = () => {
    try {
        const storedUser = localStorage.getItem('authUser');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            // Ensure essential fields exist with defaults if missing from older storage
            if (parsedUser) {
                // Ensure credits field exists
                if (parsedUser.credits === undefined) {
                    parsedUser.credits = null;
                }
                // Ensure tokens field exists
                if (parsedUser.tokens === undefined) {
                    parsedUser.tokens = null;
                }
                // Ensure onboarding_complete field exists (default to false)
                if (parsedUser.onboarding_complete === undefined) {
                     parsedUser.onboarding_complete = false;
                }
                // Ensure onboarding_form_shown field exists (default to false)
                if (parsedUser.onboarding_form_shown === undefined) {
                     parsedUser.onboarding_form_shown = false;
                }
                 // Ensure picture field exists (default to null)
                if (parsedUser.picture === undefined) {
                     parsedUser.picture = null;
                }
                // Ensure name field exists (can be null) - This holds the display name
                 if (parsedUser.name === undefined) {
                     parsedUser.name = null;
                 }
                 // Ensure cognito_username exists (can be null) - This holds the internal cognito name like google_...
                 if (parsedUser.cognito_username === undefined) {
                     parsedUser.cognito_username = null;
                 }
                // Ensure username field exists (display name from /me) - This is what dashboard reads
                 if (parsedUser.username === undefined) {
                    // Default to name if available, else null
                    parsedUser.username = parsedUser.name || null;
                 }
                 // Ensure provider field exists (login provider)
                 if (parsedUser.provider === undefined) {
                     parsedUser.provider = null;
                 }
                // Ensure amazon_status field exists (default to null)
                if (parsedUser.amazon_status === undefined) {
                     parsedUser.amazon_status = null;
                }
                // Ensure questionnaire_complete field exists (default to false)
                if (parsedUser.questionnaire_complete === undefined) {
                     parsedUser.questionnaire_complete = false;
                }
                // Ensure account_type field exists (default to 'close')
                if (parsedUser.account_type === undefined) {
                     parsedUser.account_type = 'close';
                }
            }
            return parsedUser;
        }
        return null;
    } catch (e) {
        localStorage.removeItem('authUser'); // Clear corrupted data
        return null;
    }
};

// Check if token is expired
const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Convert to seconds
        
        if (decodedToken.exp && decodedToken.exp < currentTime) {
            return true;
        }
        return false;
    } catch (e) {
        return true; // Consider invalid tokens as expired
    }
};

const AuthContext = createContext({
    token: null,
    isLoggedIn: false,
    user: null,
    login: (accessToken, idToken, refreshToken, provider) => {},
    logout: () => {},
    updateUser: (userData) => {},
    fetchUserData: () => {},
    showSessionExpiredModal: false,
    handleSessionExpired: () => {},
    redirectToLogin: () => {},
    dismissSessionExpiredModal: () => {},
    updateUserCredit: (newCreditAmount) => {}, // Added for credit update functionality
    updateUserTokens: (newTokenAmount) => {}, // Added for token update functionality
});

export const AuthProvider = (props) => {
    const [token, setToken] = useState(localStorage.getItem('authToken'));
    const [user, setUser] = useState(getInitialUser());
    const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
    
    const isLoggedIn = !!token && !isTokenExpired(token);

    // Function to handle redirection to login page
    const redirectToLogin = useCallback(() => {
        // Store the current path to redirect back after login
        const currentPath = window.location.pathname + window.location.search;
        // Don't save login/auth paths to avoid redirect loops
        if (currentPath && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
            localStorage.setItem('returnUrl', currentPath);
        }
        
        // Redirect to auth page
        window.location.href = '/auth';
    }, []);

    // Function to handle session expiration
    const handleSessionExpired = useCallback(() => {
        // Clean up authentication state
        setToken(null);
        setUser(null);
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginProvider');
        
        // Show the session expired modal
        setShowSessionExpiredModal(true);
    }, []);

    // Function to dismiss the session expired modal
    const dismissSessionExpiredModal = useCallback(() => {
        setShowSessionExpiredModal(false);
    }, []);

    // NEW: Function to specifically update user credit
    const updateUserCredit = useCallback((newCreditAmount) => {
        if (typeof newCreditAmount !== 'number') {
            return;
        }

        setUser(prevUser => {
            if (!prevUser) {
                return prevUser;
            }
            
            // Create new user object with updated credits
            const updatedUser = {
                ...prevUser,
                credits: newCreditAmount
            };

            // Update in localStorage
            localStorage.setItem('authUser', JSON.stringify(updatedUser));
            
            return updatedUser;
        });
    }, []);

    // NEW: Function to specifically update user tokens
    const updateUserTokens = useCallback((newTokenAmount) => {
        if (typeof newTokenAmount !== 'number') {
            return;
        }

        setUser(prevUser => {
            if (!prevUser) {
                return prevUser;
            }

            // Create new user object with updated tokens
            const updatedUser = {
                ...prevUser,
                tokens: newTokenAmount
            };

            // Update in localStorage
            localStorage.setItem('authUser', JSON.stringify(updatedUser));

            return updatedUser;
        });
    }, []);

    // Handler to update user state, ensuring type safety
    const updateUserHandler = useCallback((userData) => {
        if (userData && typeof userData === 'object') {

            // Ensure specific fields have correct types or defaults before saving
            const finalUserData = {
                ...userData, // Start with all received data
                // Ensure 'username' (display name) is string or null
                username: typeof userData.username === 'string' ? userData.username : null,
                // Ensure 'name' is string or null
                name: typeof userData.name === 'string' ? userData.name : null,
                // Ensure 'cognito_username' is string or null
                cognito_username: typeof userData.cognito_username === 'string' ? userData.cognito_username : null,
                // Ensure credits is number or null
                credits: typeof userData.credits === 'number' ? userData.credits : null,
                // Ensure tokens is number or null
                tokens: typeof userData.tokens === 'number' ? userData.tokens : null,
                // Ensure onboarding_complete is boolean
                onboarding_complete: userData.onboarding_complete === true,
                // Ensure picture is string or null
                picture: typeof userData.picture === 'string' ? userData.picture : null,
                // Ensure email is string or null
                email: typeof userData.email === 'string' ? userData.email : null,
                // Ensure sub is string or null
                sub: typeof userData.sub === 'string' ? userData.sub : null,
                // Ensure provider is string or null
                provider: typeof userData.provider === 'string' ? userData.provider : 
                          localStorage.getItem('loginProvider') || null,
                amazon_status: typeof userData.amazon_status === 'boolean' ? userData.amazon_status : null,
                questionnaire_complete: userData.questionnaire_complete === true,
                account_type: typeof userData.account_type === 'string' ? userData.account_type : 'close',
            };

            // Merge with existing state potentially, or just overwrite
            setUser(prevUser => ({
                ...prevUser, // Keep existing fields if userData is partial (though /me should be complete)
                ...finalUserData // Overwrite with new, type-safe data
            }));
            localStorage.setItem('authUser', JSON.stringify(finalUserData)); // Store the *final* processed data
        } else {
        }
    }, []);

    // Function to fetch user data from /me endpoint
    const fetchUserData = useCallback(async (currentToken) => {
        if (!currentToken) {
            return null;
        }

        // Check if token is expired before making request
        if (isTokenExpired(currentToken)) {
            handleSessionExpired();
            return null;
        }

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await axios.get(`${API_URL}/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${currentToken}`,
                    'Content-Type': 'application/json'
                }
            });


            // Assuming successful response (status 2xx) contains the user data directly
            if (response.data) {
                const userData = response.data; // Get data directly
                
                // Check for login provider
                const loginProvider = localStorage.getItem('loginProvider');
                if (loginProvider && !userData.provider) {
                    userData.provider = loginProvider;
                }
                
                // Update the user state with the full data including credits, display name etc.
                updateUserHandler(userData); // Use the enhanced updateUserHandler
                return userData;
            } else {
                // This case might not be reachable if axios throws for non-2xx
                return null;
            }
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                
                // If error is 401/403, token might be invalid - show session expired
                if (error.response.status === 401 || error.response.status === 403) {
                    handleSessionExpired();
                }
            } else if (error.request) {
                // The request was made but no response was received
            } else {
                // Something happened in setting up the request that triggered an Error
            }
            return null;
        }
    }, [updateUserHandler, handleSessionExpired]);

    // Guard so React Strict Mode / double-invoke does not trigger duplicate /auth/me (mount + login)
    const fetchUserDataInFlightRef = useRef(false);

    const loginHandler = useCallback((accessToken, idToken, refreshToken, provider = null) => {
        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('idToken', idToken);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }
        setToken(accessToken);
        
        // Store login provider if provided
        if (provider) {
            localStorage.setItem('loginProvider', provider);
        } else {
            // Check if we had a pending login provider
            const pendingProvider = localStorage.getItem('pendingLoginProvider');
            if (pendingProvider) {
                localStorage.setItem('loginProvider', pendingProvider);
                localStorage.removeItem('pendingLoginProvider');
            }
        }

        try {
            if (idToken) {
                const decodedToken = jwtDecode(idToken);

                // Extract basic user info from ID Token (provides initial state)
                const basicUserInfo = {
                    username: decodedToken['cognito:username'] || decodedToken.preferred_username || decodedToken.email || decodedToken.sub,
                    email: decodedToken.email || null,
                    sub: decodedToken.sub || null,
                    credits: null, // Initialize credits
                    tokens: null, // Initialize tokens
                    onboarding_complete: false, // Initialize onboarding status
                    name: decodedToken.name || null, // Attempt to get name from token
                    picture: decodedToken.picture || null, // Attempt to get picture from token
                    cognito_username: decodedToken['cognito:username'] || null, // Store original cognito username if present
                    provider: provider || localStorage.getItem('loginProvider') || null // Store login provider
                };

                setUser(basicUserInfo); // Set basic info first
                localStorage.setItem('authUser', JSON.stringify(basicUserInfo)); // Store basic info

                // Fetch full user data (including prioritized display name, credits etc.) — guard duplicate on login
                if (fetchUserDataInFlightRef.current) return;
                fetchUserDataInFlightRef.current = true;
                fetchUserData(accessToken)
                    .then(userData => {
                        if (userData) {
                        } else {
                        }
                    })
                    .catch(error => {
                    })
                    .finally(() => {
                        fetchUserDataInFlightRef.current = false;
                    });
            } else {
                setUser(null);
                localStorage.removeItem('authUser');
            }
        } catch (e) {
            setUser(null);
            localStorage.removeItem('authUser');
        }
    }, [fetchUserData]);

    const logoutHandler = useCallback(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        redirectToLogout(); // Handles Cognito logout redirect
    }, []);

    // Effect to check token expiration on mount and periodically
    useEffect(() => {
        const currentToken = localStorage.getItem('authToken');
        
        // Check token validity on app start
        if (currentToken) {
            if (isTokenExpired(currentToken)) {
                handleSessionExpired();
            } else {
                // Set token state if not already set (e.g., on page refresh)
                if (!token) {
                    setToken(currentToken);
                }
                if (fetchUserDataInFlightRef.current) return;
                fetchUserDataInFlightRef.current = true;
                fetchUserData(currentToken)
                    .then(userData => {
                        if (!userData) {
                        } 
                    })
                    .catch(error => {
                    })
                    .finally(() => {
                        fetchUserDataInFlightRef.current = false;
                    });
            }
        } else {
            if (token || user) {
                setToken(null);
                setUser(null);
            }
        }
        
        // Set up periodic token expiration check (every minute)
        if (currentToken) {
            const checkInterval = setInterval(() => {
                const token = localStorage.getItem('authToken');
                if (token && isTokenExpired(token)) {
                    handleSessionExpired();
                    clearInterval(checkInterval); // Stop checking once expired
                }
            }, 60000); // Check every minute
            
            return () => clearInterval(checkInterval);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect to sync state if localStorage is cleared externally
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'authToken' && !event.newValue && isLoggedIn) {
                logoutHandler();
            }
            if (event.key === 'authUser' && !event.newValue && user) {
                setUser(null);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [user, isLoggedIn, logoutHandler]);

    // Set up axios interceptor to catch auth errors globally
    useEffect(() => {
        const responseInterceptor = axios.interceptors.response.use(
            response => response,
            error => {
                // Handle 401/403 errors globally
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    
                    const currentToken = localStorage.getItem('authToken');
                    if (currentToken && isTokenExpired(currentToken)) {
                        handleSessionExpired();
                    } else if (currentToken) {
                        // Token is valid but we got 401/403 - handle unauthorized
                    }
                }
                return Promise.reject(error);
            }
        );
        
        return () => {
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [handleSessionExpired]);

    const contextValue = {
        token: token,
        isLoggedIn: isLoggedIn,
        user: user,
        login: loginHandler,
        logout: logoutHandler,
        updateUser: updateUserHandler,
        fetchUserData: fetchUserData,
        showSessionExpiredModal: showSessionExpiredModal,
        handleSessionExpired: handleSessionExpired,
        redirectToLogin: redirectToLogin,
        dismissSessionExpiredModal: dismissSessionExpiredModal,
        updateUserCredit: updateUserCredit, // Added the new credit update method
        updateUserTokens: updateUserTokens, // Added the new token update method
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthContext;